import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Database, Download, MessageCircle, RefreshCw } from 'lucide-react'
import axios from 'axios'

import { Button } from '../components/ui/button'
import { useAuth } from '../hooks/useAuth'
import {
  askAdminReports, getAdminReport, getReportCatalog, listAdminReports, runAdminReport,
  type AdminReport, type ReportCatalog, type ReportMessage, type ReportResult
} from '../lib/api/adminReports'

const operatorLabels: Record<string, string> = {
  eq: 'is', neq: 'is not', gte: 'at least / on or after', lte: 'at most / on or before',
  lastDays: 'within the last', isNull: 'is missing', notNull: 'has a value'
}

const example = 'Show me all the users who joined in the last 5 days and chose the custom goal.'
const errorMessage = (error: unknown) => axios.isAxiosError(error)
  ? error.response?.data?.message ?? 'The report could not be loaded. Please try again.'
  : 'The report could not be loaded. Please try again.'

// Escape spreadsheet formulas as well as ordinary CSV quotes.
export const reportCsvCell = (value: unknown) => {
  const text = value == null ? '' : String(value)
  return `"${(/^[\s]*[=+@-]/.test(text) ? "'" + text : text).replaceAll('"', '""')}"`
}

export const AdminReportsPage = () => {
  const { user } = useAuth()
  // Account changes remount all report and conversation state, including pending callbacks.
  return <ReportWorkspace key={user?.id ?? user?.email ?? 'signed-out'} />
}

const ReportWorkspace = () => {
  const { reportId } = useParams()
  const navigate = useNavigate()
  const [catalog, setCatalog] = useState<ReportCatalog | null>(null)
  const [saved, setSaved] = useState<AdminReport[]>([])
  const [report, setReport] = useState<AdminReport | null>(null)
  const [result, setResult] = useState<ReportResult | null>(null)
  const [messages, setMessages] = useState<ReportMessage[]>([])
  const [prompt, setPrompt] = useState('')
  const [error, setError] = useState('')
  const [asking, setAsking] = useState(false)
  const [loading, setLoading] = useState(false)
  const request = useRef<AbortController | null>(null)
  const query = useRef<AbortController | null>(null)
  const generation = useRef(0)
  const ownNavigation = useRef<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    void Promise.all([getReportCatalog(controller.signal), listAdminReports(controller.signal)])
      .then(([nextCatalog, nextSaved]) => {
        if (!controller.signal.aborted) { setCatalog(nextCatalog); setSaved(nextSaved) }
      }).catch(e => { if (!controller.signal.aborted) setError(errorMessage(e)) })
    return () => { controller.abort(); request.current?.abort(); query.current?.abort() }
  }, [])

  useEffect(() => {
    const current = ++generation.current
    request.current?.abort()
    query.current?.abort()
    setAsking(false)
    setError('')
    setReport(null)
    setResult(null)
    if (ownNavigation.current !== reportId) { setMessages([]); setPrompt('') }
    ownNavigation.current = null
    if (!reportId) { setLoading(false); return }
    const controller = new AbortController()
    query.current = controller
    setLoading(true)
    void getAdminReport(reportId, controller.signal).then(async next => {
      if (current !== generation.current || controller.signal.aborted) return
      setReport(next)
      const rows = await runAdminReport(reportId, 0, undefined, controller.signal)
      if (current === generation.current && !controller.signal.aborted) setResult(rows)
    }).catch(e => {
      if (current === generation.current && !controller.signal.aborted) setError(errorMessage(e))
    }).finally(() => { if (current === generation.current && !controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [reportId])

  const ask = async () => {
    const question = prompt.trim()
    if (!question || asking || loading || !catalog?.isConfigured) return
    const current = generation.current
    const controller = new AbortController()
    request.current = controller
    const history = messages.slice(-16)
    setAsking(true)
    setError('')
    try {
      const response = await askAdminReports(question, history, report?.id, controller.signal)
      if (current !== generation.current || controller.signal.aborted) return
      setMessages([...history, { role: 'user', content: question }, { role: 'assistant', content: response.message }])
      setPrompt('')
      if (response.type === 'report' && response.report) {
        const next = response.report
        setSaved(previous => [next, ...previous.filter(item => item.id !== next.id)].slice(0, 100))
        ownNavigation.current = next.id
        navigate(`/reports/${next.id}`)
      }
    } catch (e) {
      if (current === generation.current && !controller.signal.aborted) setError(errorMessage(e))
    } finally {
      if (current === generation.current && !controller.signal.aborted) setAsking(false)
    }
  }

  const loadPage = async (page: number, refresh = false) => {
    if (!report || loading) return
    query.current?.abort()
    const controller = new AbortController()
    query.current = controller
    const current = generation.current
    setLoading(true)
    setError('')
    const asOf = refresh ? undefined : result?.asOf
    setResult(null)
    try {
      const next = await runAdminReport(report.id, page, asOf, controller.signal)
      if (current === generation.current && !controller.signal.aborted) setResult(next)
    } catch (e) {
      if (current === generation.current && !controller.signal.aborted) setError(errorMessage(e))
    } finally {
      if (current === generation.current && !controller.signal.aborted) setLoading(false)
    }
  }

  const cancel = () => {
    request.current?.abort()
    setAsking(false)
    setError('Request canceled. A report may have been saved if the server finished first; reload saved reports to check.')
  }
  const label = (field: string) => catalog?.datasets[report?.definition.dataset ?? '']?.find(f => f.id === field)?.label ?? field
  const exportPage = () => {
    if (!result || !report) return
    const columns = report.definition.columns
    const csv = [columns.map(label), ...result.rows.map(row => columns.map(c => row[c]))]
      .map(row => row.map(reportCsvCell).join(',')).join('\r\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `admin-report-${result.environment}-${report.id}-page-${result.page + 1}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold"><Database className="h-6 w-6" /> Admin Reports</h2>
          <p className="mt-2 text-sm text-muted-foreground">Ask a question, inspect the filters, and open a saved report.</p>
        </div>
        <span className="rounded-full border bg-muted px-4 py-2 text-sm font-semibold">
          {catalog ? `${catalog.environment.toUpperCase()} · Read-only queries` : 'Connecting…'}
        </span>
      </header>
      {error && <p role="alert" className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">{error}</p>}
      <div className="grid items-start gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
        <aside className="space-y-5">
          <section className="rounded-xl border bg-card p-5">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 font-semibold"><MessageCircle className="h-4 w-4" /> Ask your data</h3>
              <Button variant="ghost" size="sm" onClick={() => {
                request.current?.abort(); query.current?.abort(); generation.current++
                setMessages([]); setPrompt(''); setError(''); setAsking(false); setLoading(false)
                navigate('/reports')
              }}>New chat</Button>
            </div>
            <p className="mb-4 text-xs leading-relaxed text-muted-foreground">Users, workout summaries, and stored subscriptions. Filter one dataset at a time. Current goal selections are available; signup-time goal history is not.</p>
            {messages.length === 0 && <button className="mb-4 rounded-lg border bg-muted/40 p-3 text-left text-sm hover:bg-muted" onClick={() => setPrompt(example)}>{example}</button>}
            <div role="log" aria-label="Report conversation" className="max-h-80 space-y-3 overflow-y-auto">
              {messages.map((message, index) => <div key={index} className={`rounded-lg p-3 text-sm ${message.role === 'user' ? 'bg-primary/10' : 'bg-muted/50'}`}>
                <p className="mb-1 text-xs font-semibold">{message.role === 'user' ? 'You' : 'Report assistant'}</p>
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>)}
            </div>
            <form className="mt-4 space-y-3" onSubmit={event => { event.preventDefault(); void ask() }}>
              <label htmlFor="report-question" className="block text-sm font-medium">{report ? 'Refine this report' : 'Your question'}</label>
              <textarea id="report-question" value={prompt} onChange={event => setPrompt(event.target.value)} maxLength={4000} rows={4}
                className="w-full rounded-lg border bg-background p-3 text-sm" placeholder="Who joined in the last 5 days?" />
              <div className="flex gap-2">
                <Button type="submit" disabled={!prompt.trim() || asking || loading || !catalog?.isConfigured}>{asking ? 'Building report…' : 'Ask'}</Button>
                {asking && <Button type="button" variant="outline" onClick={cancel}>Cancel</Button>}
              </div>
              {catalog && !catalog.isConfigured && <p className="text-sm text-muted-foreground">The AI service is not configured. Saved reports can still run.</p>}
            </form>
          </section>
          <section className="rounded-xl border bg-card p-5">
            <h3 className="mb-3 font-semibold">Saved reports <span className="text-xs font-normal text-muted-foreground">Latest 100 · shared with admins</span></h3>
            {saved.length === 0 && <p className="text-sm text-muted-foreground">Your report pages will appear here.</p>}
            <ul className="max-h-72 space-y-2 overflow-y-auto">{saved.map(item => <li key={item.id}>
              <Link to={`/reports/${item.id}`} className={`block rounded-lg p-2 text-sm hover:bg-muted ${item.id === reportId ? 'bg-primary/10 font-semibold' : ''}`}>
                {item.definition.title}<span className="mt-1 block text-xs text-muted-foreground">{item.environment} · {new Date(item.createdAt).toLocaleDateString()}</span>
              </Link>
            </li>)}</ul>
          </section>
        </aside>
        <section className="min-w-0 rounded-xl border bg-card p-5" aria-label="Report output" aria-busy={loading}>
          {!report && !loading && <div className="py-20 text-center"><Database className="mx-auto mb-4 h-10 w-10 text-muted-foreground" /><h3 className="text-lg font-semibold">A report starts with a question</h3><p className="mt-2 text-sm text-muted-foreground">Results come directly from this environment’s database.</p></div>}
          {report && <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><h3 className="text-xl font-semibold">{report.definition.title}</h3><p className="mt-1 text-sm text-muted-foreground">{report.environment.toUpperCase()} · {report.definition.dataset}</p></div>
              <div className="flex gap-2"><Button variant="outline" size="sm" disabled={loading} onClick={() => void loadPage(0, true)}><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
                <Button variant="outline" size="sm" disabled={!result || loading} onClick={exportPage}><Download className="mr-2 h-4 w-4" />Export this page</Button></div>
            </div>
            <div className="my-5 rounded-lg bg-muted/50 p-3 text-sm">
              <p className="font-semibold">Applied filters (all must match)</p>
              {report.definition.filters.length === 0 ? <p>No filters — all records in this dataset.</p> : <ul className="mt-2 space-y-1">{report.definition.filters.map((filter, i) => <li key={i}>{label(filter.field)} · {operatorLabels[filter.operator] ?? filter.operator} {filter.value ?? ''}{filter.operator === 'lastDays' ? ' days' : ''}</li>)}</ul>}
              <p className="mt-2 text-xs text-muted-foreground">Sorted by {label(report.definition.sortBy)} ({report.definition.sortDirection === 'asc' ? 'ascending' : 'descending'}). Dates use UTC. Last days means a rolling 24-hour window per day. Missing values appear as —.</p>
            </div>
          </>}
          {loading && <p role="status" className="py-10 text-center text-sm text-muted-foreground">Querying the database…</p>}
          {report && result && <>
            <p className="mb-1 text-lg font-semibold">{result.total.toLocaleString()} matching {report.definition.dataset}</p>
            <p className="mb-4 text-xs text-muted-foreground">Window ends {new Date(result.asOf).toISOString()} · Live results may change between pages.</p>
            {result.rows.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No matching records on this page.</p> : <div className="overflow-x-auto">
              <table className="w-full text-left text-sm"><thead><tr>{report.definition.columns.map(column => <th key={column} className="whitespace-nowrap border-b p-3">{label(column)}</th>)}</tr></thead>
                <tbody>{result.rows.map((row, index) => <tr key={index} className="border-b last:border-0">{report.definition.columns.map(column => <td key={column} className="max-w-xs break-words p-3">{row[column] == null ? '—' : String(row[column])}</td>)}</tr>)}</tbody>
              </table>
            </div>}
            <div className="mt-4 flex items-center justify-between gap-2 text-sm"><span>Page {result.page + 1} · {result.rows.length} shown</span><div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={result.page === 0} onClick={() => void loadPage(result.page - 1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={(result.page + 1) * result.pageSize >= result.total || result.page >= 10000} onClick={() => void loadPage(result.page + 1)}>Next</Button>
            </div></div>
          </>}
        </section>
      </div>
    </div>
  )
}
